import { Routes } from '@angular/router';
import { permisoGuard } from '../core/auth/permiso.guard';
import { Permiso } from '../core/auth/permisos';

/** Sección a la que pertenecen estas pantallas (encabezado de la bandeja). */
const SECCION = 'Bacteriología/Micología (BAC/MIC)';

/**
 * Sección de Bacteriología/Micología. Empieza con el monitoreo microbiológico
 * de ambiente, cuyo único formato reporta los tres servicios que el RT-52 cobra
 * por separado. Los demás servicios de la sección (aislamientos, antibiogramas,
 * microbiológico de agua) se agregan aquí con su bandeja y su hoja cuando se
 * construya su formato, y la revisión de resultados los recibe sin cambios: es
 * una sola bandeja para todas las secciones.
 */
export const bacteriologiaRoutes: Routes = [
  {
    path: 'bacteriologia/monitoreo-ambiental',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Monitoreo Microbiológico de Ambiente',
    data: {
      pantalla: 'MONITOREO_AMBIENTAL',
      breadcrumb: SECCION,
      titulo: 'Monitoreo Microbiológico de Ambiente',
      permiso: Permiso.MONITOREO_AMBIENTAL_VER,
    },
  },
  {
    path: 'bacteriologia/monitoreo-ambiental/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'MONITOREO_AMBIENTAL',
      permiso: [Permiso.MONITOREO_AMBIENTAL_RESULTADOS, Permiso.MONITOREO_AMBIENTAL_VER],
    },
    loadComponent: () =>
      import(
        './pages/monitoreo-ambiental-resultados/monitoreo-ambiental-resultados.page'
      ).then((m) => m.MonitoreoAmbientalResultadosPage),
    title: 'SiLabVet | Informe de resultados — Monitoreo Microbiológico de Ambiente',
  },
  {
    path: 'bacteriologia/bacteriologico-agua',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Bacteriológico de Agua',
    data: {
      pantalla: 'BACTERIOLOGICO_AGUA',
      breadcrumb: SECCION,
      titulo: 'Bacteriológico de Agua',
      permiso: Permiso.BACTERIOLOGICO_AGUA_VER,
    },
  },
  {
    path: 'bacteriologia/bacteriologico-agua/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'BACTERIOLOGICO_AGUA',
      permiso: [Permiso.BACTERIOLOGICO_AGUA_RESULTADOS, Permiso.BACTERIOLOGICO_AGUA_VER],
    },
    loadComponent: () =>
      import(
        './pages/bacteriologico-agua-resultados/bacteriologico-agua-resultados.page'
      ).then((m) => m.BacteriologicoAguaResultadosPage),
    title: 'SiLabVet | Informe de resultados — Bacteriológico de Agua',
  },
  {
    path: 'bacteriologia/aislamiento-bacteriano',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Aislamiento e Identificación Bacteriana',
    data: {
      pantalla: 'AISLAMIENTO_BACTERIANO',
      breadcrumb: SECCION,
      titulo: 'Aislamiento e Identificación Bacteriana',
      permiso: Permiso.AISLAMIENTO_BACTERIANO_VER,
    },
  },
  {
    path: 'bacteriologia/aislamiento-bacteriano/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'AISLAMIENTO_BACTERIANO',
      permiso: [
        Permiso.AISLAMIENTO_BACTERIANO_RESULTADOS,
        Permiso.AISLAMIENTO_BACTERIANO_VER,
      ],
    },
    loadComponent: () =>
      import(
        './pages/aislamiento-bacteriano-resultados/aislamiento-bacteriano-resultados.page'
      ).then((m) => m.AislamientoBacterianoResultadosPage),
    title: 'SiLabVet | Informe de resultados — Aislamiento e Identificación Bacteriana',
  },
  {
    path: 'bacteriologia/cultivo-salmonella-hongos',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Cultivo por Salmonella, E. coli y hongos',
    data: {
      pantalla: 'CULTIVO_SALMONELLA_HONGOS',
      breadcrumb: SECCION,
      titulo: 'Cultivo por Salmonella, E. coli y hongos',
      permiso: Permiso.CULTIVO_SALMONELLA_HONGOS_VER,
    },
  },
  {
    path: 'bacteriologia/cultivo-salmonella-hongos/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'CULTIVO_SALMONELLA_HONGOS',
      permiso: [
        Permiso.CULTIVO_SALMONELLA_HONGOS_RESULTADOS,
        Permiso.CULTIVO_SALMONELLA_HONGOS_VER,
      ],
    },
    loadComponent: () =>
      import(
        './pages/cultivo-salmonella-hongos-resultados/cultivo-salmonella-hongos-resultados.page'
      ).then((m) => m.CultivoSalmonellaHongosResultadosPage),
    title: 'SiLabVet | Informe de resultados — Cultivo por Salmonella, E. coli y hongos',
  },
  {
    path: 'bacteriologia/aislamiento-bacterias-hongos',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Aislamiento e Identificación de Bacterias y Hongos',
    data: {
      pantalla: 'AISLAMIENTO_BACTERIAS_HONGOS',
      breadcrumb: SECCION,
      titulo: 'Aislamiento e Identificación de Bacterias y Hongos',
      permiso: Permiso.AISLAMIENTO_BACTERIAS_HONGOS_VER,
    },
  },
  {
    path: 'bacteriologia/aislamiento-bacterias-hongos/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'AISLAMIENTO_BACTERIAS_HONGOS',
      permiso: [
        Permiso.AISLAMIENTO_BACTERIAS_HONGOS_RESULTADOS,
        Permiso.AISLAMIENTO_BACTERIAS_HONGOS_VER,
      ],
    },
    loadComponent: () =>
      import(
        './pages/aislamiento-bacterias-hongos-resultados/aislamiento-bacterias-hongos-resultados.page'
      ).then((m) => m.AislamientoBacteriasHongosResultadosPage),
    title:
      'SiLabVet | Informe de resultados — Aislamiento e Identificación de Bacterias y Hongos',
  },
  {
    path: 'bacteriologia/aislamiento-m-bovis',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Aislamiento e Identificación de M. bovis',
    data: {
      pantalla: 'AISLAMIENTO_M_BOVIS',
      breadcrumb: SECCION,
      titulo: 'Aislamiento e Identificación de M. bovis',
      permiso: Permiso.AISLAMIENTO_M_BOVIS_VER,
    },
  },
  {
    path: 'bacteriologia/aislamiento-m-bovis/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'AISLAMIENTO_M_BOVIS',
      permiso: [
        Permiso.AISLAMIENTO_M_BOVIS_RESULTADOS,
        Permiso.AISLAMIENTO_M_BOVIS_VER,
      ],
    },
    loadComponent: () =>
      import(
        './pages/aislamiento-m-bovis-resultados/aislamiento-m-bovis-resultados.page'
      ).then((m) => m.AislamientoMBovisResultadosPage),
    title:
      'SiLabVet | Informe de resultados — Aislamiento e Identificación de M. bovis',
  },
  {
    path: 'bacteriologia/aislamiento-salmonella',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Cultivo por Salmonella',
    data: {
      pantalla: 'AISLAMIENTO_SALMONELLA',
      breadcrumb: SECCION,
      titulo: 'Cultivo por Salmonella',
      permiso: Permiso.AISLAMIENTO_SALMONELLA_VER,
    },
  },
  {
    path: 'bacteriologia/aislamiento-salmonella/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'AISLAMIENTO_SALMONELLA',
      permiso: [
        Permiso.AISLAMIENTO_SALMONELLA_RESULTADOS,
        Permiso.AISLAMIENTO_SALMONELLA_VER,
      ],
    },
    loadComponent: () =>
      import(
        './pages/aislamiento-salmonella-resultados/aislamiento-salmonella-resultados.page'
      ).then((m) => m.AislamientoSalmonellaResultadosPage),
    title: 'SiLabVet | Informe de resultados — Cultivo por Salmonella',
  },
  {
    path: 'bacteriologia/recuento-mesofilas-hongos',
    canActivate: [permisoGuard],
    loadComponent: () =>
      import('../shared/analisis/bandeja-diagnostico/bandeja-diagnostico.page').then(
        (m) => m.BandejaDiagnosticoPage,
      ),
    title: 'SiLabVet | Determinación cuantitativa de bacterias mesófilas y hongos',
    data: {
      pantalla: 'RECUENTO_MESOFILAS_HONGOS',
      breadcrumb: SECCION,
      titulo: 'Determinación cuantitativa de bacterias mesófilas y hongos',
      permiso: Permiso.RECUENTO_MESOFILAS_HONGOS_VER,
    },
  },
  {
    path: 'bacteriologia/recuento-mesofilas-hongos/:id/trabajar',
    canActivate: [permisoGuard],
    data: {
      pantalla: 'RECUENTO_MESOFILAS_HONGOS',
      permiso: [
        Permiso.RECUENTO_MESOFILAS_HONGOS_RESULTADOS,
        Permiso.RECUENTO_MESOFILAS_HONGOS_VER,
      ],
    },
    loadComponent: () =>
      import(
        './pages/recuento-mesofilas-hongos-resultados/recuento-mesofilas-hongos-resultados.page'
      ).then((m) => m.RecuentoMesofilasHongosResultadosPage),
    title:
      'SiLabVet | Informe de resultados — Determinación cuantitativa de bacterias mesófilas y hongos',
  },
];
